import {
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { keyboardStepDelta, swipeStepDelta, wrapStep } from './steppingRules';

type SwipeStart = { pointerId: number; x: number; y: number };

type BannerStepping = {
  /** For the banner's root element. */
  rootRef: RefObject<HTMLElement | null>;
  /** For the dot of the request on show. */
  currentDotRef: RefObject<HTMLButtonElement | null>;
  /** Move `delta` requests from the one on show, wrapping at both ends. */
  stepBy: (delta: number) => void;
  /** Jump to a request (counted from 1). */
  stepTo: (target: number) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  swipeHandlers: {
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLElement>) => void;
    onPointerCancel: () => void;
  };
};

type Options = { step: number; total: number; onStep: (nextStep: number) => void };

// A text field (an input, a text area or a select) or an editable area needs the arrow keys for
// itself. A menu or a slider would too, but the banner holds none, so none is looked for.
function usesArrowKeys(target: EventTarget): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * The ways a customer steps through the open requests, other than the buttons themselves: the
 * arrow keys while focus is in the banner, and a swipe with a finger or pen. All of them end in
 * `onStep`. The banner holds no step; the parent does.
 *
 * Stepping with focus in the banner can take the focused dot out of the five on show. The browser
 * then drops focus to the page, and the arrow keys would stop working. So when a step was asked
 * for from inside the banner and focus is gone once the new step shows, focus moves to the dot of
 * the request now on show.
 */
export function useBannerStepping({ step, total, onStep }: Options): BannerStepping {
  const rootRef = useRef<HTMLElement | null>(null);
  const currentDotRef = useRef<HTMLButtonElement | null>(null);
  const swipeStart = useRef<SwipeStart | null>(null);
  const focusToKeepAtStep = useRef<number | null>(null);
  const forgetFocusFrame = useRef<number | null>(null);
  const hasSomewhereToGo = total > 1;

  useEffect(
    () => () => {
      if (forgetFocusFrame.current !== null) {
        cancelAnimationFrame(forgetFocusFrame.current);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    const shouldKeepFocus = focusToKeepAtStep.current === step;
    focusToKeepAtStep.current = null;
    const root = rootRef.current;
    if (shouldKeepFocus && root && !root.contains(document.activeElement)) {
      currentDotRef.current?.focus();
    }
  }, [step]);

  // The parent shows the step it is given while it handles `onStep`. A step that has not shown by
  // the next frame was ignored, and focus must not be taken for it if the parent shows that
  // request later, for reasons of its own.
  const forgetTheFocusToKeepAfterThisFrame = () => {
    if (forgetFocusFrame.current !== null) {
      cancelAnimationFrame(forgetFocusFrame.current);
    }
    forgetFocusFrame.current = requestAnimationFrame(() => {
      forgetFocusFrame.current = null;
      focusToKeepAtStep.current = null;
    });
  };

  const stepTo = (target: number) => {
    if (target === step) {
      return;
    }
    const hasFocusInside = rootRef.current?.contains(document.activeElement) ?? false;
    focusToKeepAtStep.current = hasFocusInside ? target : null;
    onStep(target);
    forgetTheFocusToKeepAfterThisFrame();
  };

  const stepBy = (delta: number) => stepTo(wrapStep(step, delta, total));

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const delta = hasSomewhereToGo
      ? keyboardStepDelta({
          key: event.key,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          defaultPrevented: event.defaultPrevented,
          targetHandlesArrows: usesArrowKeys(event.target),
        })
      : 0;
    if (delta !== 0) {
      event.preventDefault();
      stepBy(delta);
    }
  };

  const swipeHandlers = {
    // Only the first finger starts a swipe, so a second touch cannot add up to one.
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (event.isPrimary) {
        swipeStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      }
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      const start = swipeStart.current;
      if (!start || start.pointerId !== event.pointerId) {
        return;
      }
      swipeStart.current = null;
      const delta = hasSomewhereToGo
        ? swipeStepDelta({
            dx: event.clientX - start.x,
            dy: event.clientY - start.y,
            pointerType: event.pointerType,
          })
        : 0;
      if (delta !== 0) {
        stepBy(delta);
      }
    },
    onPointerCancel: () => {
      swipeStart.current = null;
    },
  };

  return { rootRef, currentDotRef, stepBy, stepTo, onKeyDown, swipeHandlers };
}
